<template>
  <div class="sm-avatar">
    <v-tooltip
      location="top"
      :disabled="!tooltipText"
    >
      <template #activator="{ props }">
        <v-avatar
          :size="size"
          :rounded="tile ? 0 : (roundedTile ? 'lg' : undefined)"
          v-bind="props"
        >
          <v-skeleton-loader
            v-if="showLoading"
            :width="size"
            :height="size"
            type="image"
          />
          <v-img
            v-if="deactivated && !showLoading"
            :height="size"
            :width="size"
            cover
            :src="deactivatedAvatar"
          />
          <v-img
            v-else-if="showImage && !showLoading"
            :src="finalImageUrl"
            :height="size"
            :width="size"
            cover
            @error="imgLoadingError = true"
          />
          <v-icon
            v-else-if="anonymous && !showLoading"
            :size="size"
          >
            ico-sm-avatar-anonymous
          </v-icon>
          <v-icon
            v-if="!anonymous && !showImage && !showLoading"
          >
            ico-sm-user
          </v-icon>
        </v-avatar>
      </template>
      <span class="text-pre-wrap">{{ tooltipText }}</span>
    </v-tooltip>
  </div>
</template>

<script lang="ts">
import defaultUserImage from '@src/assets/img/ico-sm-avatar-default.svg'
import deactivatedAvatar from '@src/assets/img/deactivated-avatar.png'
import { computed, defineComponent, ref } from 'vue'
import useUser from '@src/composables/user'

export default defineComponent({
  name: 'SMAvatar',
  props: {
    size: {
      type: Number,
      default: 30
    },
    resolutionFactor: {
      type: Number,
      default: 2
    },
    anonymous: {
      type: Boolean,
      default: false
    },
    deactivated: {
      type: Boolean,
      default: false
    },
    self: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: undefined
    },
    tooltipText: {
      type: String,
      default: ''
    },
    tile: {
      type: Boolean,
      default: false
    },
    roundedTile: {
      type: Boolean,
      default: false
    },
    showLoading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { userFetchError, userLoading, user } = useUser()
    const imgLoadingError = ref(false)

    const selfImageUrl = computed((): string | undefined => (
      !userLoading.value && !userFetchError.value ? user.value.image || undefined : undefined
    ))
    const customImageUrl = computed((): string | undefined => props.url || undefined)
    const finalImageUrl = computed((): string | undefined => {
      const unprocessedUrl = props.self ? selfImageUrl.value : customImageUrl.value
      if (unprocessedUrl) {
        const url = new URL(unprocessedUrl)
        const scaledSize = props.size * props.resolutionFactor
        url.searchParams.set('nocrop', 'false')
        url.searchParams.set('width', `${scaledSize}`)
        url.searchParams.set('height', `${scaledSize}`)
        return url.toString()
      }
      return defaultUserImage
    })
    const showImage = computed((): boolean => !props.anonymous && !!finalImageUrl.value && !imgLoadingError.value)

    return {
      userFetchError, userLoading, user, imgLoadingError, deactivatedAvatar,
      finalImageUrl, showImage
    }
  }
})
</script>
